import {BrandingStepKey, CardStyle, CornerRadius} from "src/model/v2/interface/props";

export const REQUIRED_MSG = 'This field is required'
export const PAGE_PADDING_X = '3.2rem'
export const PAGE_PADDING_Y = '2.4rem'

export const fontLight   = { fontFamily: 'Gilmer Light', fontWeight: 300 }
export const fontRegular = { fontFamily: 'Gilmer Regular', fontWeight: 400 }
export const fontMedium  = { fontFamily: 'Gilmer Medium', fontWeight: 500 }
export const fontBold    = { fontFamily: 'Gilmer Bold', fontWeight: 700 }
export const fontHeavy   = { fontFamily: 'Gilmer Heavy', fontWeight: 800 }
export const fontLabel = { fontFamily: 'Poppins', fontWeight: 400 }
export const fontStatus = { fontFamily: 'Inter', fontWeight: 500 }

export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024
export const MAX_FILE_SIZE_KB = MAX_FILE_SIZE_BYTES / 1024
export const LOGO_ACCEPT = '.png,.jpg,.jpeg,.svg'
export const LOGO_HINT = `PNG, JPG, JPEG or SVG, max ${MAX_FILE_SIZE_KB / 1024}MB`
export const FILE_TOO_LARGE_MSG = `File exceeds max size of ${MAX_FILE_SIZE_KB / 1024}MB`

export const ROLE_OPTIONS = [
    { label: 'Admin', value: 'Admin' },
    { label: 'Institution Super Admin', value: 'ISA' },
    { label: 'User', value: 'User' },
]

export const ROLE_LABEL: Record<string, string> = Object.fromEntries(
    ROLE_OPTIONS.map((o) => [o.value, o.label])
)

export const VARIANT_API_MAP: Record<string, 1 | 2 | 3> = { one: 1, two: 2, three: 3 }
export const VARIANT_FROM_API: Record<number, string> = { 1: 'one', 2: 'two', 3: 'three' }

export const CORNER_RADIUS_API_MAP: Record<CornerRadius, 'Sharp' | 'Standard' | 'Round'> = {
    '0px': 'Sharp',
    '8px': 'Standard',
    '24px': 'Round',
}
export const CORNER_RADIUS_FROM_API: Record<string, CornerRadius> = {
    Sharp: '0px',
    Standard: '8px',
    Round: '24px',
}

export const TYPOGRAPHY_API_MAP: Record<string, string> = {
    modern: 'Inter',
    classic: 'Georgia',
}
export const TYPOGRAPHY_FROM_API: Record<string, string> = {
    Inter: 'modern',
    Georgia: 'classic',
}

export const CARD_STYLE_API_MAP: Record<CardStyle, 'Solid' | 'Gradient' | 'PatternOverlay'> = {
    solid: 'Solid',
    gradient: 'Gradient',
    pattern: 'PatternOverlay',
}
export const CARD_STYLE_FROM_API: Record<string, CardStyle> = {
    Solid: 'solid',
    Gradient: 'gradient',
    PatternOverlay: 'pattern',
}

export const CORNER_OPTIONS: { value: CornerRadius; label: string }[] = [
    { value: '0px', label: 'Sharp (0px)' },
    { value: '8px', label: 'Standard (8px)' },
    { value: '24px', label: 'Round (24px)' },
]
export const CARD_STYLE_OPTIONS: { value: CardStyle; title: string; help: string }[] = [
    { value: 'solid', title: 'Solid Color', help: 'Uses primary brand color' },
    { value: 'gradient', title: 'Gradient', help: 'Linear gradient from primary to secondary' },
    { value: 'pattern', title: 'Pattern Overlay', help: 'Custom pattern overlay' },
]
export const TYPOGRAPHY_OPTIONS = [
    { label: 'Modern (Inter / Montserrat)', value: 'modern', fontFamily: "'Gilmer Regular', sans-serif" },
    { label: 'Classic (Georgia / Times)', value: 'classic', fontFamily: "Georgia, 'Times New Roman', serif" },
]

export const BRANDING_STEPS = ['Select Theme', 'Configure Theme', 'Onboarding Slides']
export const BRANDING_STEP_KEYS: BrandingStepKey[] = ['BrandingDesign', 'BrandingTheme', 'OnboardingSlide']
export const MIN_SLIDES = 3
export const MAX_SLIDES = 5

export const THEME_COLORS_STORAGE_KEY = 'qore.institutionBranding.colors'

// ---- Create Institution page ----
// Moved from CreateInstitution.tsx so the page's copy/options live alongside
// the rest of the app's shared constants instead of as inline literals.

export const COUNTRY_CATEGORY_OPTIONS = [
    { label: 'Nigeria', value: 'NG' },
    { label: 'Ghana', value: 'GH' },
    { label: 'Kenya', value: 'KE' },
    { label: 'France', value: 'FR' },
]

export const BAAS_TOGGLE_LABEL = 'BaaS Institution'

// Odd one-off design values that were previously hardcoded inline on the
// page — pulled out here so any future page can reuse or override them
// rather than re-typing magic strings.
export const BACK_LINK_COLOR = '#000000'
export const HEADER_TITLE_GAP = '1.2'   // gap between back link + page title (Flex prop, unitless -> rem)
export const HEADER_ACTIONS_GAP = '1.6' // gap between toggle + primary action button (Flex prop, unitless -> rem)
export const CARD_PADDING = '3.2'       // Card padding prop (unitless -> rem)
export const CARD_TOP_MARGIN = '2.4'    // Card topMargin prop (unitless -> rem)